// src/components/checkout/CheckoutModal.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from './StripePaymentForm';

const stripePromise = loadStripe('pk_test_51RLr2VPVJCHLYuow88qQY3POrujoEElTdzJsUdACLtKdyJMHC0GOyj9F6cvQLN810g7cxgfohYhkzYH2Jtvqy38I00fGCvbX3j');

/* Les villes gardent leur valeur française pour l'API, seuls les labels sont traduits */
const MOROCCAN_CITIES = [
  { value: 'Casablanca', key: 'casablanca' },
  { value: 'Rabat',      key: 'rabat' },
  { value: 'Fès',        key: 'fes' },
  { value: 'Marrakech',  key: 'marrakech' },
  { value: 'Tanger',     key: 'tanger' },
  { value: 'Agadir',     key: 'agadir' },
  { value: 'Meknès',     key: 'meknes' },
  { value: 'Oujda',      key: 'oujda' },
  { value: 'Kénitra',    key: 'kenitra' },
  { value: 'Tétouan',    key: 'tetouan' },
  { value: 'Salé',       key: 'sale' },
  { value: 'Autre',      key: 'autre' },
];

function CheckoutModal({ cartItems, subTotal, onClose, onSuccess }) {
  const { t } = useTranslation();

  const [step, setStep]                   = useState(1);
  const [name, setName]                   = useState('');
  const [phone, setPhone]                 = useState('');
  const [city, setCity]                   = useState('');
  const [address, setAddress]             = useState('');
  const [note, setNote]                   = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState(null);
  const [orderNumber, setOrderNumber]     = useState(null);

  const [shippingFee, setShippingFee] = useState(0);
  const [loadingFee, setLoadingFee]   = useState(false);

  const total = subTotal + shippingFee;

  useEffect(() => {
    if (!city) {
      setShippingFee(0);
      return;
    }
    setLoadingFee(true);
    axios.post('https://madadline.com/api/shipping/calculate', { city })
      .then(({ data }) => setShippingFee(Number(data.fee) || 0))
      .catch(() => setShippingFee(0))
      .finally(() => setLoadingFee(false));
  }, [city]);

  const validateInfos = () => {
    if (!name.trim())    { setError(t('checkout.errorName'));    return false; }
    if (!phone.trim())   { setError(t('checkout.errorPhone'));   return false; }
    if (!city)           { setError(t('checkout.errorCity'));    return false; }
    if (!address.trim()) { setError(t('checkout.errorAddress')); return false; }
    return true;
  };

  const buildPayload = (extraFields = {}) => ({
    customer_name:  name,
    customer_phone: phone,
    city:           city,
    address:        address,
    note:           note || '',
    payment_method: 'cod',
    country:        'Maroc',
    currency:       'MAD',
    cart_items: cartItems.map(i => ({
      product_id: i.product.id,
      quantity:   i.quantity,
      size:       i.size || null,
      color:      i.color || null,
      material:   i.material || null,
    })),
    ...extraFields,
  });

  const clearCart = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        await axios.delete('https://madadline.com/api/cart/clear', {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.warn('Panier non vidé côté serveur:', e?.response?.data || e);
      }
    }
    localStorage.removeItem('cart');
  };

  const handleError = (err, defaultMsg) => {
    console.error('Erreur commande:', err.response?.data || err);
    const serverErrors = err.response?.data?.errors;
    const firstError = serverErrors
      ? Object.values(serverErrors)[0]?.[0]
      : (err.response?.data?.error || err.response?.data?.message);
    setError(firstError || defaultMsg);
  };

  const handleCODOrder = async () => {
    setError(null);
    if (!validateInfos()) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.post(
        'https://madadline.com/api/orders',
        buildPayload({ payment_method: 'cod' }),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await clearCart();
      setOrderNumber(data?.order?.id || 'MADAD-' + Date.now());
      setStep(2);
    } catch (err) {
      handleError(err, t('checkout.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId) => {
    setError(null);
    if (!validateInfos()) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.post(
        'https://madadline.com/api/orders',
        buildPayload({ payment_method: 'stripe', payment_intent_id: paymentIntentId }),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await clearCart();
      setOrderNumber(data?.order?.id || 'MADAD-' + Date.now());
      setStep(2);
    } catch (err) {
      handleError(err, t('checkout.errorStripe'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransferOrder = async () => {
    setError(null);
    if (!validateInfos()) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.post(
        'https://madadline.com/api/orders',
        buildPayload({ payment_method: 'transfer' }),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await clearCart();
      setOrderNumber(data?.order?.id || 'MADAD-' + Date.now());
      setStep(2);
    } catch (err) {
      handleError(err, t('checkout.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const msg = `${t('checkout.waIntro')}\n\n` +
      cartItems.map(i => `• ${i.product.nom} × ${i.quantity}`).join('\n') +
      `\n\n${t('checkout.waSubtotal')} : ${subTotal.toFixed(0)} DH` +
      `\n${t('checkout.waShipping')} : ${shippingFee.toFixed(0)} DH` +
      `\n${t('checkout.waTotal')} : ${total.toFixed(0)} DH` +
      `\n\n${t('checkout.waName')} : ${name}` +
      `\n${t('checkout.waPhone')} : ${phone}` +
      `\n${t('checkout.waCity')} : ${city}` +
      `\n${t('checkout.waAddress')} : ${address}` +
      (note ? `\n${t('checkout.waNote')} : ${note}` : '');

    window.open(`https://wa.me/212669428878?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleFinalClose = async () => {
    await clearCart();
    if (typeof onSuccess === 'function') onSuccess();
  };

  return (
    <div className="checkout-modal" onClick={onClose}>
      <div className="checkout-modal__inner" onClick={e => e.stopPropagation()}>
        <button className="checkout-modal__close" onClick={onClose} aria-label={t('checkout.closeAria')}>✕</button>

        {step === 1 ? (
          <>
            <header className="checkout-modal__head">
              <span className="checkout-modal__label">{t('checkout.headLabel')}</span>
              <h2 className="checkout-modal__title">{t('checkout.headTitle')}</h2>
            </header>

            {error && <div className="checkout-modal__error">{error}</div>}

            <div className="checkout-form">
              <div className="checkout-form__group">
                <label htmlFor="name">{t('checkout.labelName')}</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t('checkout.placeholderName')}
                />
              </div>

              <div className="checkout-form__group">
                <label htmlFor="phone">{t('checkout.labelPhone')}</label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder={t('checkout.placeholderPhone')}
                />
              </div>

              <div className="checkout-form__group">
                <label htmlFor="city">{t('checkout.labelCity')}</label>
                <select
                  id="city"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                >
                  <option value="">{t('checkout.selectCity')}</option>
                  {MOROCCAN_CITIES.map(c => (
                    <option key={c.value} value={c.value}>{t(`checkout.cities.${c.key}`)}</option>
                  ))}
                </select>
              </div>

              <div className="checkout-form__group">
                <label htmlFor="address">{t('checkout.labelAddress')}</label>
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder={t('checkout.placeholderAddress')}
                />
              </div>

              <div className="checkout-form__group">
                <label htmlFor="note">{t('checkout.labelNote')}</label>
                <textarea
                  id="note"
                  rows="2"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={t('checkout.placeholderNote')}
                />
              </div>

              <div className="checkout-form__group">
                <label>{t('checkout.paymentMethodLabel')}</label>
                <div className="checkout-form__radios">
                  <label className="checkout-form__radio">
                    <input
                      type="radio"
                      name="payment"
                      value="cod"
                      checked={paymentMethod === 'cod'}
                      onChange={() => setPaymentMethod('cod')}
                    />
                    <span>
                      <strong>{t('checkout.payCodTitle')}</strong>
                      <small>{t('checkout.payCodText')}</small>
                    </span>
                  </label>
                  <label className="checkout-form__radio">
                    <input
                      type="radio"
                      name="payment"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={() => setPaymentMethod('stripe')}
                    />
                    <span>
                      <strong>{t('checkout.payStripeTitle')}</strong>
                      <small>{t('checkout.payStripeText')}</small>
                    </span>
                  </label>
                  <label className="checkout-form__radio">
                    <input
                      type="radio"
                      name="payment"
                      value="transfer"
                      checked={paymentMethod === 'transfer'}
                      onChange={() => setPaymentMethod('transfer')}
                    />
                    <span>
                      <strong>{t('checkout.payTransferTitle')}</strong>
                      <small>{t('checkout.payTransferText')}</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="checkout-recap">
                <div className="checkout-recap__row">
                  <span>
                    {t('checkout.recapSubtotal', {
                      n: cartItems.length,
                      plural: cartItems.length > 1 ? 's' : '',
                    })}
                  </span>
                  <span>{subTotal.toFixed(0)} DH</span>
                </div>
                <div className="checkout-recap__row">
                  <span>
                    {t('checkout.recapShipping')} {city ? `(${city})` : ''}
                  </span>
                  <span>
                    {loadingFee
                      ? t('checkout.recapCalculating')
                      : shippingFee > 0
                        ? `${shippingFee.toFixed(0)} DH`
                        : t('checkout.recapToCalculate')}
                  </span>
                </div>
                <div className="checkout-recap__divider" />
                <div className="checkout-recap__row checkout-recap__row--total">
                  <span>{t('checkout.recapTotal')}</span>
                  <span>{total.toFixed(0)} DH</span>
                </div>
              </div>

              {paymentMethod === 'cod' && (
                <button
                  type="button"
                  className="checkout-form__submit"
                  onClick={handleCODOrder}
                  disabled={submitting}
                >
                  {submitting ? t('checkout.submitting') : t('checkout.submitCod')}
                </button>
              )}

              {paymentMethod === 'transfer' && (
                <button
                  type="button"
                  className="checkout-form__submit"
                  onClick={handleTransferOrder}
                  disabled={submitting}
                >
                  {submitting ? t('checkout.submitting') : t('checkout.submitTransfer')}
                </button>
              )}

              {paymentMethod === 'stripe' && (
                <Elements stripe={stripePromise}>
                  <StripePaymentForm
                    total={total}
                    onSuccess={handleStripeSuccess}
                    onError={(msg) => setError(msg)}
                  />
                </Elements>
              )}

              <p className="checkout-form__legal">{t('checkout.legal')}</p>
              {!localStorage.getItem('auth_token') && (
  <p style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 8 }}>
    Astuce : créez un compte pour suivre vos commandes plus facilement.
  </p>
)}
            </div>
          </>
        ) : (
          <div className="checkout-success">
            <div className="checkout-success__icon">✓</div>
            <h2 className="checkout-success__title">
              {t('checkout.successTitle', { name })}
            </h2>
            <p className="checkout-success__sub">{t('checkout.successSub')}</p>
            <div className="checkout-success__number">
              {t('checkout.successNumber', { number: orderNumber })}
            </div>
            <p className="checkout-success__info">{t('checkout.successInfo')}</p>

            <div className="checkout-success__actions">
              <button className="checkout-success__wa" onClick={handleWhatsApp}>
                {t('checkout.successWhatsapp')}
              </button>
              <button className="checkout-success__close" onClick={handleFinalClose}>
                {t('checkout.successClose')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckoutModal;